import { PortableText, PortableTextProps } from "next-sanity";

import type { ReactNode } from "react";

import { Link } from "@/components/link/link.component";
import { isRichTextBrandColorToken } from "@/components/richText/richTextBrandColorTokens";
import { Text } from "@/components/text/text.component";

import { resolveLinkHref } from "@/utils/url.util";

import { FeatureBlockContent } from "./featureBlockContent.component";
import { isFeatureBlockStyle } from "./featureBlockStyles.config";
import { FeatureListItem } from "./featureListItem.component";
import { RichTextAlignMark } from "./richTextAlignMark.component";
import { RichTextImage } from "./richTextImage.component";
import { RichTextInlineIcon } from "./richTextInlineIcon.component";
import { RichTextInlinePhoneNumber } from "./richTextInlinePhoneNumber.component";
import { RichTextInteractiveQueryLink } from "./richTextInteractiveQueryLink.component";

const linkUnderlineClassName = (disableUnderline?: boolean | null) =>
  disableUnderline === true ? undefined : "underline";

export const RichText = ({
  children,
  asH1,
  interactiveQueryParam,
  blockComponents,
  listComponents,
  listItemComponents,
  typeComponents,
}: RichTextProps) => {
  if (!children) return null;
  return (
    <PortableText
      value={children}
      components={{
        block: {
          h1: ({ children }) => (
            <Text variant="h1" component={asH1 ? "h1" : undefined}>
              {children}
            </Text>
          ),
          h2: ({ children }) => (
            <Text variant="h2" component={asH1 ? "h1" : undefined}>
              {children}
            </Text>
          ),
          h3: ({ children }) => <Text variant="h3">{children}</Text>,
          h4: ({ children }) => <Text variant="h4">{children}</Text>,
          h5: ({ children }) => <Text variant="h5">{children}</Text>,
          h6: ({ children }) => <Text variant="h6">{children}</Text>,
          subtitle1: ({ children }) => (
            <Text variant="subtitle1">{children}</Text>
          ),
          subtitle2: ({ children }) => (
            <Text variant="subtitle2">{children}</Text>
          ),
          preHeaderLg: ({ children }) => (
            <Text variant="preHeaderLg">{children}</Text>
          ),
          preHeaderSm: ({ children }) => (
            <Text variant="preHeaderSm">{children}</Text>
          ),
          body1: ({ children }) => (
            <Text variant="body1" component="p">
              {children}
            </Text>
          ),
          body2: ({ children }) => (
            <Text variant="body2" component="p">
              {children}
            </Text>
          ),
          body3: ({ children }) => (
            <Text variant="body3" component="p">
              {children}
            </Text>
          ),
          body4: ({ children }) => (
            <Text variant="body4" component="p">
              {children}
            </Text>
          ),
          body5: ({ children }) => (
            <Text variant="body5" component="p">
              {children}
            </Text>
          ),
          body6: ({ children }) => (
            <Text variant="body6" component="p">
              {children}
            </Text>
          ),
          legal: ({ children }) => (
            <Text variant="legal" component="p">
              {children}
            </Text>
          ),
          chips: ({ children }) => (
            <Text variant="chips" component="p">
              {children}
            </Text>
          ),
          legalTextWhite: ({ children }) => (
            <Text variant="legal" component="p" className="text-white">
              {children}
            </Text>
          ),
          featureIncluded: ({ children, value }) => (
            <FeatureBlockContent
              style="featureIncluded"
              isListItem={Boolean(value?.listItem)}>
              {children}
            </FeatureBlockContent>
          ),
          featureNotIncluded: ({ children, value }) => (
            <FeatureBlockContent
              style="featureNotIncluded"
              isListItem={Boolean(value?.listItem)}>
              {children}
            </FeatureBlockContent>
          ),
          featureExcluded: ({ children, value }) => (
            <FeatureBlockContent
              style="featureExcluded"
              isListItem={Boolean(value?.listItem)}>
              {children}
            </FeatureBlockContent>
          ),
          featureThinCheck: ({ children, value }) => (
            <FeatureBlockContent
              style="featureThinCheck"
              isListItem={Boolean(value?.listItem)}>
              {children}
            </FeatureBlockContent>
          ),
          normal: ({ children }) => {
            // If the child is an empty string, return a <br /> tag
            if (
              Array.isArray(children) &&
              children.length === 1 &&
              children[0] === ""
            ) {
              return <br />;
            }
            return <Text component="p">{children}</Text>;
          },
          ...blockComponents,
        },
        list: {
          bullet: ({ children }) => (
            <ul className="flex w-full list-disc flex-col gap-2 pl-8 max-lg:has-[.rich-text-feature-icon-item]:mt-8 max-lg:has-[.rich-text-feature-icon-item]:list-none max-lg:has-[.rich-text-feature-icon-item]:pl-0">
              {children}
            </ul>
          ),
          number: ({ children }) => (
            <ol className="flex list-decimal flex-col gap-2 pl-8">
              {children}
            </ol>
          ),
          ...listComponents,
        },
        listItem: {
          bullet: ({ children, value }) => {
            const style = value?.style;

            if (isFeatureBlockStyle(style)) {
              return (
                <FeatureListItem style={style}>{children}</FeatureListItem>
              );
            }

            return <li>{children}</li>;
          },
          ...listItemComponents,
        },
        marks: {
          customLink: ({ children, value }) => {
            const link = value?.customLink;
            const href = resolveLinkHref(link);
            const target =
              link?.linkType !== "anchor" && link?.openInNewTab
                ? "_blank"
                : undefined;
            return (
              <Link
                href={href}
                target={target}
                rel={target === "_blank" ? "noopener noreferrer" : undefined}
                className={linkUnderlineClassName(link?.disableUnderline)}>
                {children}
              </Link>
            );
          },
          documentAction: ({ children, value }) => {
            const modalId = value?.modal?._ref;

            if (interactiveQueryParam && modalId) {
              return (
                <RichTextInteractiveQueryLink
                  param={interactiveQueryParam}
                  value={modalId}
                  aria-label={value.accessibilityLabel ?? undefined}>
                  {children}
                </RichTextInteractiveQueryLink>
              );
            }
            return <span className="underline">{children}</span>;
          },
          superscript: ({ children }) => <sup>{children}</sup>,
          alignLeft: ({ children }) => (
            <RichTextAlignMark alignment="left">{children}</RichTextAlignMark>
          ),
          alignCenter: ({ children }) => (
            <RichTextAlignMark alignment="center">{children}</RichTextAlignMark>
          ),
          alignRight: ({ children }) => (
            <RichTextAlignMark alignment="right">{children}</RichTextAlignMark>
          ),
          textColor: ({ children, value }) => {
            const token = value?.token;
            if (!isRichTextBrandColorToken(token)) {
              return <>{children}</>;
            }
            return (
              <span style={{ color: `var(--color-${token})` }}>{children}</span>
            );
          },
        },
        types: {
          inlineIcon: RichTextInlineIcon,
          inlinePhoneNumber: RichTextInlinePhoneNumber,
          image: RichTextImage,
          ...typeComponents,
        },
      }}
    />
  );
};

export type RichTextProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children?: PortableTextProps<any>["value"];
  /**
   * When true, H1-styled blocks render as a semantic `<h1>`.
   * When false/undefined, H1 keeps its visual style only (default `div`).
   */
  asH1?: boolean | null;
  /**
   * When set, `documentAction` marks navigate by merging this query key with
   * the referenced content modal id (client-side; preserves existing params).
   */
  interactiveQueryParam?: string;
  blockComponents?: RichTextBlockComponents;
  listComponents?: RichTextListComponents;
  listItemComponents?: RichTextListItemComponents;
  typeComponents?: RichTextTypeComponents;
};

type RichTextTypeComponents = {
  [type: string]: (props: {
    value?: {
      text?: string;
      level?: string;
      anchorId?: string;
    };
  }) => ReactNode;
};

type RichTextListComponents = {
  bullet?: (props: { children?: ReactNode }) => ReactNode;
  number?: (props: { children?: ReactNode }) => ReactNode;
};

type RichTextListItemComponents = {
  bullet?: (props: {
    children?: ReactNode;
    value?: { style?: string; listItem?: string };
  }) => ReactNode;
};

type RichTextBlockComponents = {
  [style: string]: (props: {
    children?: ReactNode;
    value?: { _key?: string; listItem?: unknown };
  }) => ReactNode;
};
